import { colors } from '@theme/foundations/colors';
import { ColorLabelEnum } from '@utils/map/map.constants';

import {
  LadSensor,
  LadSensorCategoryEnum as NewLadSensorCategoryEnum,
  PerimeterSensor,
  SensorInstallationStatusEnum,
  SensorTypeAbbrKey,
} from '@/types/filters.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import { SensorTypeWithShorthand } from '@/types/sensor/sensorInstallation.types';
import {
  EffectiveSensorStatusEnum,
  SensorTypeEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';

export const PERIMETER_SECTION_TEXT_OFFSET_L = [0, 45];
export const SENSOR_LABEL_TEXT_SIZE = 14;
export const ROUND_SECTION_WIDTH_SCALING_FACTOR = 24;

export const sensorColorLabelFromSensorStatus: Record<
  EffectiveSensorStatusEnum | SpecificStateEnum.EFFRACTION,
  ColorLabelEnum
> = {
  UNREACHABLE: ColorLabelEnum.PURPLE,
  DEGRADED: ColorLabelEnum.ORANGE,
  KO: ColorLabelEnum.ORANGE,
  EFFRACTION: ColorLabelEnum.RED,
  HEALTHY: ColorLabelEnum.GREEN,
} as const;

export enum CategoryEnum {
  LAD = 'LAD',
  PERIM = 'PERIM',
}

export const sensorCategoryFromSensorType: Record<SensorTypeEnum | SubSensorTypeEnum, CategoryEnum> = {
  ADSB: CategoryEnum.LAD,
  AEROSCOPE: CategoryEnum.LAD,
  ARDRONIS: CategoryEnum.LAD,
  AUTOMATON: CategoryEnum.PERIM,
  AVISO: CategoryEnum.LAD,
  BLAST: CategoryEnum.LAD,
  DFB6: CategoryEnum.LAD,
  DRONE_SHIELD: CategoryEnum.LAD,
  FLIR: CategoryEnum.LAD,
  GENERIC: CategoryEnum.LAD,
  GROUND_STATION: CategoryEnum.LAD,
  HOLODRONE: CategoryEnum.LAD,
  AF2206: CategoryEnum.LAD,
  AXIS: CategoryEnum.LAD,
  MAJES: CategoryEnum.LAD,
  ROBIN_MAX: CategoryEnum.LAD,
  ONVIF: CategoryEnum.LAD,
  SKY_EYE: CategoryEnum.LAD,
  SKY_JACKER: CategoryEnum.LAD,
  SPEXER: CategoryEnum.LAD,
  XENTA: CategoryEnum.LAD,
  ZMER: CategoryEnum.LAD,
  SENID: CategoryEnum.LAD,
  ARDRONIS_ANTENNA: CategoryEnum.LAD,
  AUTOMATON_CABINET: CategoryEnum.PERIM,
  AUTOMATON_SEGMENT: CategoryEnum.PERIM,
  GENERIC_SUB_SENSOR: CategoryEnum.LAD,
  ROBIN_MAX_RADAR: CategoryEnum.LAD,
  SKY_EYE_ANTENNA: CategoryEnum.LAD,
  SKY_JACKER_ANTENNA: CategoryEnum.LAD,
  SPEXER_RADAR: CategoryEnum.LAD,
  SENID_ANTENNA: CategoryEnum.LAD,
} as const;

export const sensorFamilyFromSensorType: Record<SensorTypeEnum | SubSensorTypeEnum, SensorFamilyEnum> = {
  ADSB: SensorFamilyEnum.COLLABORATIVE,
  AEROSCOPE: SensorFamilyEnum.COLLABORATIVE,
  ARDRONIS: SensorFamilyEnum.RF,
  AUTOMATON: SensorFamilyEnum.AUTOMATON,
  AVISO: SensorFamilyEnum.COLLABORATIVE,
  BLAST: SensorFamilyEnum.NEUTRAL,
  DFB6: SensorFamilyEnum.NEUTRAL,
  DRONE_SHIELD: SensorFamilyEnum.RF,
  FLIR: SensorFamilyEnum.RADAR,
  GENERIC: SensorFamilyEnum.COLLABORATIVE,
  GROUND_STATION: SensorFamilyEnum.COLLABORATIVE,
  HOLODRONE: SensorFamilyEnum.COLLABORATIVE,
  AF2206: SensorFamilyEnum.CAMERA,
  AXIS: SensorFamilyEnum.CAMERA,
  MAJES: SensorFamilyEnum.NEUTRAL,
  ROBIN_MAX: SensorFamilyEnum.RADAR,
  ONVIF: SensorFamilyEnum.CAMERA,
  SKY_EYE: SensorFamilyEnum.RF,
  SKY_JACKER: SensorFamilyEnum.NEUTRAL,
  SPEXER: SensorFamilyEnum.RADAR,
  XENTA: SensorFamilyEnum.RADAR,
  ZMER: SensorFamilyEnum.CAMERA,
  SENID: SensorFamilyEnum.COLLABORATIVE,
  ARDRONIS_ANTENNA: SensorFamilyEnum.RF,
  AUTOMATON_CABINET: SensorFamilyEnum.AUTOMATON,
  AUTOMATON_SEGMENT: SensorFamilyEnum.AUTOMATON,
  GENERIC_SUB_SENSOR: SensorFamilyEnum.COLLABORATIVE,
  ROBIN_MAX_RADAR: SensorFamilyEnum.RADAR,
  SKY_EYE_ANTENNA: SensorFamilyEnum.RF,
  SKY_JACKER_ANTENNA: SensorFamilyEnum.NEUTRAL,
  SPEXER_RADAR: SensorFamilyEnum.RADAR,
  SENID_ANTENNA: SensorFamilyEnum.COLLABORATIVE,
} as const;

export const sensorStatusColor: Record<SensorInstallationStatusEnum, string> = {
  [SensorInstallationStatusEnum.OPERATIONAL]: colors.green.DEFAULT.value,
  [SensorInstallationStatusEnum.DRAFT]: colors.neutral['600'].value,
} as const;

export const SensorShorthand: Record<SensorTypeWithShorthand, string> = {
  [SensorTypeEnum.ADSB]: 'AD',
  [SensorTypeEnum.AEROSCOPE]: 'AER',
  [SensorTypeEnum.ARDRONIS]: 'ARD',
  [SensorTypeEnum.AUTOMATON]: 'API',
  [SensorTypeEnum.AVISO]: 'AVI',
  [SubSensorTypeEnum.AUTOMATON_CABINET]: 'BT',
  [SensorTypeEnum.BLAST]: 'BLAST',
  [SensorTypeEnum.DFB6]: 'D6',
  [SensorTypeEnum.FLIR]: 'FLIR',
  [SensorTypeEnum.GENERIC]: 'GEN',
  [SensorTypeEnum.GROUND_STATION]: 'GRS',
  [SensorTypeEnum.HOLODRONE]: 'HLD',
  [SensorTypeEnum.AF2206]: 'HLP',
  [SensorTypeEnum.AXIS]: 'AXIS',
  [SensorTypeEnum.MAJES]: '3B',
  [SensorTypeEnum.ROBIN_MAX]: 'RMAX',
  [SensorTypeEnum.SENID]: 'SID',
  [SensorTypeEnum.SKY_EYE]: 'SKY',
  [SensorTypeEnum.SKY_JACKER]: 'SKJ',
  [SensorTypeEnum.SPEXER]: 'SPX',
  [SubSensorTypeEnum.AUTOMATON_SEGMENT]: 'TRO',
  [SensorTypeEnum.XENTA]: 'XTA',
  [SensorTypeEnum.ZMER]: 'ZMER',
} as const;

export const ReverseSensorShorthand = Object.fromEntries(
  Object.entries(SensorShorthand).map(([fullType, abbr]) => [abbr, fullType]),
) as Record<SensorTypeAbbrKey, SensorTypeWithShorthand>;

export const LadSensorCategoryMapping: Record<NewLadSensorCategoryEnum, string[]> = {
  [NewLadSensorCategoryEnum.COLLABORATIVE]: [
    SensorShorthand[SensorTypeEnum.ADSB],
    SensorShorthand[SensorTypeEnum.AEROSCOPE],
    SensorShorthand[SensorTypeEnum.AVISO],
    SensorShorthand[SensorTypeEnum.GENERIC],
    SensorShorthand[SensorTypeEnum.GROUND_STATION],
    SensorShorthand[SensorTypeEnum.HOLODRONE],
  ],
  [NewLadSensorCategoryEnum.RF]: [
    SensorShorthand[SensorTypeEnum.ARDRONIS],
    SensorShorthand[SensorTypeEnum.SENID],
    SensorShorthand[SensorTypeEnum.SKY_EYE],
  ],
  [NewLadSensorCategoryEnum.CAMERA]: [
    SensorShorthand[SensorTypeEnum.ZMER],
    SensorShorthand[SensorTypeEnum.AF2206],
    SensorShorthand[SensorTypeEnum.AXIS],
  ],
  [NewLadSensorCategoryEnum.RADAR]: [
    SensorShorthand[SensorTypeEnum.FLIR],
    SensorShorthand[SensorTypeEnum.ROBIN_MAX],
    SensorShorthand[SensorTypeEnum.SPEXER],
    SensorShorthand[SensorTypeEnum.XENTA],
  ],
  [NewLadSensorCategoryEnum.NEUTRAL]: [
    SensorShorthand[SensorTypeEnum.BLAST],
    SensorShorthand[SensorTypeEnum.DFB6],
    SensorShorthand[SensorTypeEnum.MAJES],
    SensorShorthand[SensorTypeEnum.SKY_JACKER],
  ],
} as const;

export function isPerimeterSensor(sensor: string): sensor is PerimeterSensor {
  return (
    sensor in sensorCategoryFromSensorType &&
    sensorCategoryFromSensorType[sensor as keyof typeof sensorCategoryFromSensorType] === CategoryEnum.PERIM
  );
}

export function isLadSensor(sensor: string): sensor is LadSensor {
  return (
    sensor in sensorCategoryFromSensorType &&
    sensorCategoryFromSensorType[sensor as keyof typeof sensorCategoryFromSensorType] === CategoryEnum.LAD
  );
}

export const PerimeterSensorShorthands: string[] = Object.entries(SensorShorthand)
  .filter(([key]) => isPerimeterSensor(key))
  .map(([, abbreviation]) => abbreviation)
  .sort();

export const LadSensorShorthands: string[] = Object.entries(SensorShorthand)
  .filter(([key]) => isLadSensor(key))
  .map(([, abbreviation]) => abbreviation)
  .sort();
